'use client'

import React from 'react'

const PLAY_STORE_LINK = 'https://play.google.com/store/apps/details?id=com.solkuu.grablocal'
const APPLE_STORE_LINK = 'https://apps.apple.com/in/app/grablocal/id6737913110'

// The GrabLocal wordmark from grablocal.in, in its rust colourway for this light
// surface; served from this app's public/images.
const LOGO_SRC = '/images/grablocal-logo-rust.svg'

// Ink tokens (mirrors grablocal-website footer — cream surface, coral glow)
const INK = '#1D1D1D'
const SOFT = 'rgba(29,29,29,0.62)'

const SOCIAL = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/grablocal_bh',
    icon: (
      <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" />
        <circle cx="12" cy="12" r="4.2" />
        <circle cx="17.4" cy="6.6" r="1.1" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    label: 'X',
    href: 'https://x.com/GrabLocal_BH',
    icon: (
      <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    label: 'YouTube',
    href: '#',
    icon: (
      <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M23.5 6.2a3.02 3.02 0 0 0-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.51A3.02 3.02 0 0 0 .5 6.2 31.4 31.4 0 0 0 0 12a31.4 31.4 0 0 0 .5 5.8 3.02 3.02 0 0 0 2.12 2.14c1.88.51 9.38.51 9.38.51s7.5 0 9.38-.51a3.02 3.02 0 0 0 2.12-2.14A31.4 31.4 0 0 0 24 12a31.4 31.4 0 0 0-.5-5.8zM9.6 15.6V8.4l6.27 3.6z" />
      </svg>
    ),
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/gr4blocal',
    icon: (
      <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M4.98 3.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM3 9h4v12H3zM9 9h3.8v1.7h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.1V21h-4v-5.4c0-1.29-.02-2.95-1.8-2.95-1.8 0-2.08 1.4-2.08 2.85V21H9z" />
      </svg>
    ),
  },
]

export default function Footer() {
  return (
    <footer
      className="relative overflow-hidden pt-16 pb-10"
      style={{
        marginTop: 72,
        background:
          'radial-gradient(82% 62% at 50% 100%, rgba(242,98,80,1) 0%, rgba(242,98,80,0) 72%), var(--rev-white)',
        color: INK,
      }}
    >
      {/* ── Top: brand (left) + CTA (right) ── */}
      <div className="gl-container relative z-10">
        <div className="grid gap-12 md:grid-cols-[1fr_1.05fr] md:gap-10 lg:gap-16">

          {/* Brand block */}
          <div className="flex flex-col items-center text-center md:items-start md:text-left">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={LOGO_SRC} alt="GrabLocal" style={{ height: 72, width: 'auto' }} />

            <p className="mt-5 max-w-[24em] text-[0.96rem] font-medium leading-6" style={{ color: SOFT }}>
              The curated marketplace for homegrown brands at zero commission, always.
            </p>

            {/* Social */}
            <div className="mt-6 flex items-center gap-4">
              {SOCIAL.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  title={s.label}
                  className="transition-colors"
                  style={{ color: 'rgba(29,29,29,0.8)' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#F26250' }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'rgba(29,29,29,0.8)' }}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* CTA block */}
          <div className="flex flex-col items-center text-center md:items-end md:text-right">
            <h2 className="tracking-tight" style={{ fontSize: 'clamp(28px, 3.4vw, 40px)', lineHeight: 1.08, fontFamily: 'var(--font-serif, Georgia, serif)', fontWeight: 500, color: INK }}>
              Start shopping{' '}
              <span style={{ fontStyle: 'italic', color: '#F26250' }}>local.</span>
            </h2>

            <p className="mt-3 max-w-md text-[13px] leading-6" style={{ color: SOFT }}>
              Download the app and discover Bharat&apos;s best homegrown brands — or manage your store from the seller portal.
            </p>

            {/* App buttons */}
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3 md:justify-end">
              <a
                href={PLAY_STORE_LINK}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition hover:scale-[1.02]"
                style={{ background: '#F26250', boxShadow: '0 10px 26px -10px rgba(242,98,80,0.7)' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.37 11.23L5.43 3.19c-.45-.26-1 .06-1 .59v16.44c0 .53.55.85 1 .59l13.94-8.04c.46-.26.46-.92 0-1.18z" />
                </svg>
                Google Play
              </a>
              <a
                href={APPLE_STORE_LINK}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition hover:scale-[1.02]"
                style={{ background: '#F26250', boxShadow: '0 10px 26px -10px rgba(242,98,80,0.7)' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>
                App Store
              </a>
            </div>

            {/* Seller link */}
            <a
              href={process.env.NEXT_PUBLIC_PORTAL_URL}
              target="_blank" rel="noopener noreferrer"
              className="mt-4 rounded-full px-5 py-2 text-[13px] transition"
              style={{ border: '1px solid rgba(29,29,29,0.18)', color: SOFT }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(29,29,29,0.45)'; e.currentTarget.style.color = INK }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(29,29,29,0.18)'; e.currentTarget.style.color = SOFT }}
            >
              Are you a seller? Access your Management Portal →
            </a>
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t pt-6 text-center text-[0.85rem] font-semibold md:justify-between md:text-left" style={{ borderColor: 'rgba(29,29,29,0.1)', color: SOFT }}>
          <span className="inline-flex items-center gap-1.5">
            Made with
            <svg width="13" height="13" viewBox="0 0 24 24" fill="#F26250" stroke="#F26250" aria-hidden="true"><path d="M12 21s-6.7-4.35-9.33-8.02C.9 10.37 1.4 6.9 4.1 5.6c1.9-.92 4.06-.3 5.4 1.3L12 9.4l2.5-2.5c1.34-1.6 3.5-2.22 5.4-1.3 2.7 1.3 3.2 4.77 1.43 7.38C18.7 16.65 12 21 12 21z" /></svg>
            in Bengaluru
          </span>
          <span>© {new Date().getFullYear()} Solkuu Technologies Pvt Ltd</span>
          <span className="inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <a
              href="mailto:support@grablocal.in"
              className="inline-flex items-center gap-1.5 leading-none transition-colors"
              style={{ color: SOFT }}
              onMouseEnter={e => { e.currentTarget.style.color = INK }}
              onMouseLeave={e => { e.currentTarget.style.color = SOFT }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="shrink-0" aria-hidden="true">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-10 6L2 7" />
              </svg>
              support@grablocal.in
            </a>
            <span aria-hidden="true" style={{ color: 'rgba(29,29,29,0.3)' }}>·</span>
            <a
              href="tel:+917676156728"
              className="inline-flex items-center gap-1.5 leading-none transition-colors"
              style={{ color: SOFT }}
              onMouseEnter={e => { e.currentTarget.style.color = INK }}
              onMouseLeave={e => { e.currentTarget.style.color = SOFT }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="shrink-0" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              +91 76761 56728
            </a>
          </span>
        </div>
      </div>
    </footer>
  )
}
